"use client";

// ============================================================================
// /global-error — Letzte Verteidigungslinie
// ----------------------------------------------------------------------------
// Wird gerendert, wenn der Root-Layout selbst kollabiert (z.B. ThemeProvider
// crasht, MotionConfig wirft, layout.tsx-Code-Fehler). Muss eigenes
// <html><body> liefern, weil das normale Layout nicht mehr verfügbar ist.
//
// Hält bewusst minimal: nur Inline-Tailwind, kein eigener Font, kein Theme.
// Sentry-Capture explizit, weil Auto-Capture ohne intaktes Layout unsicher ist.
// ============================================================================

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error, {
      tags: { boundary: "app/global-error" },
      level: "fatal",
      extra: { digest: error.digest },
    });
  }, [error]);

  return (
    <html lang="de">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
          background: "#090A0C",
          color: "#E6E8EC",
        }}
      >
        <main
          style={{
            maxWidth: "480px",
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: "10px",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#94A3B8",
              marginBottom: "16px",
            }}
          >
            500 · Schwerer Systemfehler
          </p>

          <h1
            style={{
              fontSize: "32px",
              fontWeight: 600,
              lineHeight: 1.1,
              marginBottom: "16px",
            }}
          >
            SiFa Flow konnte nicht geladen werden.
          </h1>

          <p
            style={{
              fontSize: "15px",
              color: "#CBD5E1",
              lineHeight: 1.6,
              marginBottom: "32px",
            }}
          >
            Wir haben den Fehler automatisch erfasst. Versuchen Sie die Seite
            neu zu laden — falls das nicht hilft, schreiben Sie uns an{" "}
            <a
              href="mailto:kontakt@sifaflow.de"
              style={{ color: "#FF8A3D", textDecoration: "underline" }}
            >
              kontakt@sifaflow.de
            </a>
            .
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              onClick={reset}
              style={{
                padding: "12px 20px",
                borderRadius: "6px",
                border: "1px solid #FF6B00",
                background: "#FF6B00",
                color: "#1A1A1A",
                fontWeight: 600,
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Erneut versuchen
            </button>
            {/* Reines <a> (kein next/link) — global-error ist bewusst
                dependency-frei (Root-Layout ist gecrasht), muss aber einen
                Ausstieg zur Startseite bieten. */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              href="/"
              style={{
                padding: "12px 20px",
                borderRadius: "6px",
                border: "1px solid #334155",
                background: "transparent",
                color: "#E6E8EC",
                fontWeight: 600,
                fontSize: "14px",
                textDecoration: "none",
              }}
            >
              Zur Startseite
            </a>
          </div>

          {error.digest && (
            <p
              style={{
                marginTop: "32px",
                fontFamily: "ui-monospace, monospace",
                fontSize: "11px",
                color: "#64748B",
              }}
            >
              Referenz-ID: <span style={{ userSelect: "all" }}>{error.digest}</span>
            </p>
          )}
        </main>
      </body>
    </html>
  );
}
